import { useForm } from '@inertiajs/react'
import { AppShell } from '@/components/layout/AppShell'
import { PageHeader } from '@/components/landing/PageHeader'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface Props {
  user: { email: string; role: string }
  organization: { name: string; slug: string }
  applications: { id: string; name: string }[]
  event_types: string[]
  errors: Record<string, string[]>
}

export default function New({ user, organization, applications, event_types, errors }: Props) {
  const { data, setData, post, processing } = useForm({
    application_id: '',
    url: '',
    description: '',
    subscribed_events: [] as string[],
  })

  function toggleEvent(event: string) {
    setData(
      'subscribed_events',
      data.subscribed_events.includes(event)
        ? data.subscribed_events.filter((e) => e !== event)
        : [...data.subscribed_events, event],
    )
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    post('/dashboard/webhooks')
  }

  return (
    <AppShell user={user} organization={organization}>
      <div className="max-w-2xl space-y-8">
        <PageHeader
          eyebrow="Webhooks · new"
          title="New Webhook"
          description="Configure an endpoint to receive event callbacks."
        />

        <section className="surface-card p-6">
          <h2 className="font-heading text-base font-semibold tracking-tight">
            Webhook configuration
          </h2>
          <div className="mt-5">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="application_id">Application</Label>
                <Select
                  value={data.application_id}
                  onValueChange={(val) => setData('application_id', val)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select an application" />
                  </SelectTrigger>
                  <SelectContent>
                    {applications.map((app) => (
                      <SelectItem key={app.id} value={app.id}>
                        {app.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.application_id && (
                  <p className="text-sm text-destructive">{errors.application_id[0]}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="url">Endpoint URL</Label>
                <Input
                  id="url"
                  type="url"
                  placeholder="https://your-server.com/webhooks"
                  value={data.url}
                  onChange={(e) => setData('url', e.target.value)}
                />
                {errors.url && <p className="text-sm text-destructive">{errors.url[0]}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description (optional)</Label>
                <Textarea
                  id="description"
                  placeholder="What this webhook is used for..."
                  value={data.description}
                  onChange={(e) => setData('description', e.target.value)}
                />
              </div>

              <div className="space-y-3">
                <Label>Events to subscribe</Label>
                <div className="grid grid-cols-2 gap-3">
                  {event_types.map((evt) => (
                    <label key={evt} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={data.subscribed_events.includes(evt)}
                        onCheckedChange={() => toggleEvent(evt)}
                      />
                      <span className="text-sm font-mono">{evt}</span>
                    </label>
                  ))}
                </div>
                {errors.subscribed_events && (
                  <p className="text-sm text-destructive">{errors.subscribed_events[0]}</p>
                )}
              </div>

              <Button type="submit" disabled={processing}>
                {processing ? 'Creating…' : 'Create Webhook'}
              </Button>
            </form>
          </div>
        </section>
      </div>
    </AppShell>
  )
}
